import {
    CustomizationCorners,
    CustomizationThemeMode,
    SITE_TITLE_MAX_LENGTH,
    SITE_TITLE_MIN_LENGTH,
} from '@gitbook/api-client';
import { type ContentThemeMode, SPACE_DEFAULT_TITLE } from '@gitbook/app-database';
import { colorScale } from '@gitbook/colors';
import { Icon } from '@gitbook/spine-icons';
import { Alert, Description, FieldGroup, Label, TextInput, Tooltip } from '@gitbook/spine-react';

import { NavigationLink } from '../../../navigation';
import { ContentEditableIcon } from '../../Content';
import type { SitesCustomizationPanelProps } from '../types';
import { getHeaderPreviewBackground, getHeaderPreviewForeground } from './utils';

/**
 * Settings to configure the published title.
 */
export function TitleSettings(
    props: SitesCustomizationPanelProps & { siteSpaceId?: string; siteSpaceName?: string }
) {
    const { settings, setSettings, siteSpaceId } = props;

    return (
        <FieldGroup>
            <Label>Title</Label>
            <Description>
                The name of your site. Used in metadata like the browser tab title and link
                previews.
                {settings.header.logo ? '' : ' Also displayed in the site header.'}
            </Description>
            <TextInput
                minLength={SITE_TITLE_MIN_LENGTH}
                maxLength={SITE_TITLE_MAX_LENGTH}
                placeholder={props.site.title}
                value={siteSpaceId ? (props.siteSpaceName ?? '') : (settings.title ?? '')}
                disabled={Boolean(siteSpaceId)}
                onChange={(event) => {
                    const title = event.currentTarget.value || undefined;
                    setSettings((prev) => ({
                        ...prev,
                        title,
                    }));
                }}
            />
            {siteSpaceId ? (
                <Alert
                    kind="info"
                    size="xsmall"
                    details={
                        <p>
                            To edit a section/variant title, go to{' '}
                            <NavigationLink
                                to={{
                                    overlay: null,
                                    screen: {
                                        type: 'site',
                                        site: props.site.id,
                                        organization: props.organizationId,
                                        tab: { type: 'settings', section: 'structure' },
                                    },
                                }}
                            >
                                site structure settings
                            </NavigationLink>
                            .
                        </p>
                    }
                />
            ) : null}
        </FieldGroup>
    );
}

export function IconSettings(props: SitesCustomizationPanelProps) {
    const { settings } = props;
    return (
        <FieldGroup>
            <Label>Icon</Label>
            <Description>
                Used as the site's{' '}
                <Tooltip label="A favicon (favorite icon) is displayed in places like the browser's address bar, page tabs and bookmarks.">
                    <abbr className="underline decoration-dotted underline-offset-4">favicon</abbr>
                </Tooltip>
                {!settings.header.logo ? ' and in the site header' : ''}.
            </Description>
            <div className="flex gap-4">
                {Object.values(CustomizationThemeMode).map((themeMode) => (
                    <IconEditable key={themeMode} {...props} themeMode={themeMode} />
                ))}
            </div>
            {'emoji' in settings.favicon ? (
                <Alert
                    details="Emoji icons are the same across light and dark mode."
                    kind="info"
                    size="xsmall"
                />
            ) : null}
        </FieldGroup>
    );
}

function IconEditable(props: SitesCustomizationPanelProps & { themeMode: ContentThemeMode }) {
    const { settings, setSettings, organizationId, site, themeMode } = props;
    const title = settings.title ?? site.title;

    const headerBackground = getHeaderPreviewBackground(settings, themeMode);
    const headerForeground = getHeaderPreviewForeground(settings, themeMode);
    const iconColors = colorScale(settings.styling.primaryColor[themeMode], {
        darkMode: themeMode === CustomizationThemeMode.Dark,
    });

    return (
        <div className="flex flex-1 shrink-0 flex-col gap-2">
            <ContentEditableIcon
                uploadTo={{
                    kind: 'site_icon',
                    organization: organizationId,
                    site: site.id,
                }}
                onIconURLChange={(publishedIconURL) => {
                    setSettings((prev) => ({
                        ...prev,
                        favicon: {
                            icon: {
                                ...(prev.favicon && 'icon' in prev.favicon
                                    ? prev.favicon.icon
                                    : {
                                          light: publishedIconURL,
                                          dark: publishedIconURL,
                                      }),
                                [themeMode]: publishedIconURL,
                            },
                        },
                    }));
                }}
                onEmojiChange={(publishedEmoji) => {
                    setSettings((prev) => ({
                        ...prev,
                        favicon: { emoji: publishedEmoji },
                    }));
                }}
                onClear={() => {
                    setSettings((prev) => ({
                        ...prev,
                        favicon: {},
                    }));
                }}
                iconURL={'icon' in settings.favicon ? settings.favicon.icon[themeMode] : undefined}
                emoji={'emoji' in settings.favicon ? settings.favicon.emoji : undefined}
                size={32}
                fallbackText={title ?? SPACE_DEFAULT_TITLE}
                style={{
                    background: headerBackground,
                    color: headerForeground,
                }}
                itemStyle={{
                    background: `linear-gradient(to bottom, ${iconColors[themeMode === CustomizationThemeMode.Light ? 2 : 6]}, ${iconColors[4]})`,
                    color: iconColors[10],
                    border: `1px solid ${iconColors[5]}`,
                    borderRadius:
                        settings.styling.corners === CustomizationCorners.Rounded
                            ? '0.25rem'
                            : settings.styling.corners === CustomizationCorners.Circular
                              ? '50%'
                              : '0',
                }}
                className="h-16 w-full py-4"
            />
            <div className="flex items-center gap-2 text-muted">
                {themeMode === CustomizationThemeMode.Light ? (
                    <>
                        <Icon.ThemeLight size={16} className="shrink-0" />
                        <div className="text-ui-small">Light mode</div>
                    </>
                ) : (
                    <>
                        <Icon.ThemeDark size={16} className="shrink-0" />
                        <div className="text-ui-small">Dark mode</div>
                    </>
                )}
            </div>
        </div>
    );
}
